import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ModalComponent from "../modalComponent";
import { useForm } from "react-hook-form";
import TextInput from "../textInput";
import ButtonComponent from "../button";
import {
  callApiAsync,
  createAddittionalCost,
  getCurrency,
} from "../../services/apiService";
import SearchableSelectInput from "../selectInput/searchableSelectInput";
import { Controller } from "react-hook-form";
import { Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import ToastNotification from "../toastNotification";
import {
  CURRENCY_COP,
  transformCurrencyData,
} from "../../utils/currencyUtils";

const schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("El nombre de la característica es obligatorio"),
  description: yup.string().trim().required("La descripción es obligatoria"),
  requirementId: yup
    .string()
    .trim()
    .required("El ID de requerimiento es obligatorio"),
  cost: yup
    .number()
    .typeError("El costo debe ser un número válido")
    .positive("El costo debe ser mayor a 0")
    .required("El costo es obligatorio"),
  price: yup
    .number()
    .typeError("El precio debe ser un número válido")
    .positive("El precio debe ser mayor a 0")
    .required("El precio es obligatorio"),
  tax: yup
    .number()
    .typeError("El impuesto debe ser un número válido")
    .min(0, "El impuesto no puede ser negativo")
    .nullable(),
  numberOfPayments: yup
    .number()
    .typeError("El número de pagos debe ser un número válido")
    .positive("El número de pagos debe ser mayor a 0")
    .nullable(),
  isPaid: yup.boolean().required("El estado de pago es obligatorio"),
  currency: yup.string().required("La moneda es obligatoria"),
});

const AddAddittionalModal = ({
  open,
  onClose,
  billingPeriodId = null,
  businessUnitId = null,
  getAddittionalCostDataTable,
  setAdditionalCost,
  additionalCost,
}) => {
  const userData = useSelector((state) => state.user?.userData);
  const isReadOnly = additionalCost && additionalCost.isPaid;
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");
  const [currencyData, setCurrencyData] = useState([]);

  const formDefaultValues = {
    name: "",
    description: "",
    requirementId: "",
    cost: 0,
    price: 0,
    tax: null,
    numberOfPayments: null,
    isPaid: false,
    currency: String(CURRENCY_COP),
  };
  const { control, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: formDefaultValues,
  });

  const onCloseModal = () => {
    reset(formDefaultValues);
    onClose();
    setAdditionalCost(null);
  };

  const setData = (data) => {
    if (!data) return;

    setValue("name", data.name);
    setValue("description", data.description);
    setValue("requirementId", data.requirementId || "");
    setValue("cost", data.cost || 0);
    setValue("price", data.price);
    setValue("tax", data.tax || null);
    setValue("numberOfPayments", data.numberOfPayments || null);
    setValue("isPaid", data.isPaid || false);
    setValue("currency", String(data.currency ?? CURRENCY_COP));
  };

  const showToast = (message) => {
    setToastSeverity("error");
    setToastMessage(message);
    setToastOpen(true);
  };

  const validatePaymentAmount = (dataForm) => {
    if (!dataForm.isPaid) return true;

    if (!additionalCost) {
      showToast(
        "No se puede marcar como pagado porque no hay pagos registrados."
      );
      return false;
    }

    const actualPaid = additionalCost.totalValuePaid;
    if (!actualPaid) {
      showToast(
        "No se puede marcar como pagado porque no hay pagos registrados."
      );
      return false;
    }

    const expectedTotal = dataForm.price + (dataForm.tax ? dataForm.tax : 0);

    if (Math.abs(expectedTotal - actualPaid) > 0.01) {
      showToast(
        `El monto pagado ($${actualPaid.toFixed(
          2
        )}) no coincide con el monto esperado ($${expectedTotal.toFixed(
          2
        )}). No se puede marcar como pagado.`
      );
      return false;
    } else {
      return true;
    }
  };

  const handleFormSubmit = async (formData, id) => {
    if (isReadOnly) {
      onCloseModal();
      return;
    }

    if (!validatePaymentAmount(formData)) {
      return;
    }

    const dataToSend = {
      id: additionalCost ? additionalCost.id : 0,
      billingPeriodId: billingPeriodId || 0,
      businessUnitId: businessUnitId || 0,
      ...formData,
      currency: parseInt(formData.currency, 10) || CURRENCY_COP,
      userEmail: userData?.email ?? "",
    };

    await callApiAsync(
      () => createAddittionalCost(dataToSend),
      (data) => {
        getAddittionalCostDataTable();
      }
    );

    onCloseModal();
  };

  useEffect(() => {
    setData(additionalCost);
  }, [additionalCost]);

  useEffect(() => {
    if (!open) return;

    callApiAsync(
      () => getCurrency(),
      (data) => setCurrencyData(transformCurrencyData(data))
    );
  }, [open]);

  return (
    <ModalComponent
      customWidth={"600px"}
      open={open}
      onClose={onCloseModal}
      title={`${
        additionalCost ? (isReadOnly ? "Ver" : "Editar") : "Agregar"
      } costos independientes`}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <TextInput
          name="name"
          control={control}
          label="Título"
          disabled={isReadOnly}
        />
        <TextInput
          name="description"
          control={control}
          label="Descripción"
          isTextArea={true}
          rows={6}
          maxLength={1000}
          disabled={isReadOnly}
        />
        <TextInput
          name="requirementId"
          control={control}
          label="ID de Requerimiento"
          margin="10px 0 0 0"
          disabled={isReadOnly}
        />
        <TextInput
          name="cost"
          control={control}
          label="Costo"
          margin="10px 0 0 0"
          type="number"
          disabled={isReadOnly}
        />
        <TextInput
          name="price"
          control={control}
          label="Precio"
          margin="10px 0 0 0"
          type="number"
          disabled={isReadOnly}
        />
        <div style={{ paddingTop: "15px" }}>
          <SearchableSelectInput
            name="currency"
            control={control}
            label="Moneda"
            options={currencyData}
            disabled={isReadOnly}
          />
        </div>
        <TextInput
          name="tax"
          control={control}
          label="Impuesto"
          margin="10px 0 0 0"
          type="number"
          disabled={isReadOnly}
        />
        <TextInput
          name="numberOfPayments"
          control={control}
          label="Número de Pagos"
          margin="10px 0 0 0"
          type="number"
          disabled={isReadOnly}
        />
        <div style={{ paddingTop: "15px" }}>
          <Controller
            name="isPaid"
            control={control}
            defaultValue={false}
            render={({ field, fieldState: { error } }) => (
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={isReadOnly}
                    />
                  }
                  label="Pagado"
                />
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </div>
            )}
          />
        </div>
        <div className="HContainer j-center mt-20">
          <ButtonComponent
            type="submit"
            backgroundColor={isReadOnly ? "#ccc" : "#F6C079"}
            textColor="#000000"
            width="160px"
            disabled={isReadOnly}
          >
            {isReadOnly ? "Cerrar" : "Guardar"}
          </ButtonComponent>
        </div>
      </form>
      <ToastNotification
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </ModalComponent>
  );
};

export default AddAddittionalModal;
